
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Game } from '@/types/game';
import { slugToTitle } from '@/utils/gameUtils';

const fetchGame = async (searchParam: string | undefined, isSlugRoute: boolean = false): Promise<Game> => {
  if (!searchParam) throw new Error('Game parameter is required');

  console.log('Fetching game with param:', searchParam, 'isSlugRoute:', isSlugRoute);

  if (isSlugRoute) {
    // For slug-based routes, be more precise in matching
    const titleFromSlug = slugToTitle(searchParam);
    console.log('Converted slug to title:', titleFromSlug);
    
    // Try exact title match first (case-insensitive)
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .ilike('title', titleFromSlug)
      .eq('hidden', false)
      .limit(1)
      .maybeSingle();
    
    if (data) {
      console.log('Found game by exact title match:', data.title);
      return mapGameData(data);
    }
    
    // If no exact match, try a more flexible approach
    // Split the slug into words and search for games containing most words
    const words = searchParam.split('-').filter(word => word.length > 2);
    if (words.length > 0) {
      console.log('Trying word-based search with words:', words);
      
      // Create a search query that looks for games containing the key words
      const searchQuery = words.join(' & ');
      
      const { data: searchData, error: searchError } = await supabase
        .from('games')
        .select('*')
        .textSearch('title', searchQuery)
        .eq('hidden', false)
        .limit(5); // Get multiple results to find the best match
      
      if (searchData && searchData.length > 0) {
        // Find the best match by checking how many words from the slug appear in the title
        let bestMatch = searchData[0];
        let bestScore = 0;
        
        for (const game of searchData) {
          const gameTitle = game.title.toLowerCase();
          const score = words.reduce((acc, word) => {
            return acc + (gameTitle.includes(word.toLowerCase()) ? 1 : 0);
          }, 0);
          
          if (score > bestScore) {
            bestScore = score;
            bestMatch = game;
          }
        }
        
        console.log('Found best match:', bestMatch.title, 'with score:', bestScore);
        return mapGameData(bestMatch);
      }
    }
    
    console.error('Game not found for slug:', searchParam);
    throw new Error('Game not found');
  } else {
    // ID-based lookup - this should be straightforward
    console.log('Fetching by ID:', searchParam);
    
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .eq('id', searchParam)
      .eq('hidden', false)
      .single();
    
    if (error || !data) {
      console.error('Game not found by ID:', searchParam, error);
      throw new Error('Game not found');
    }
    
    console.log('Found game by ID:', data.title);
    return mapGameData(data);
  }
};

// Helper function to map database game to Game type
const mapGameData = (dbGame: any): Game => {
  return {
    id: dbGame.id,
    title: dbGame.title,
    description: dbGame.description || '',
    thumbnail: dbGame.thumbnail || '',
    category: dbGame.category,
    featured: dbGame.featured || false,
    trending: dbGame.trending || false,
    isNew: dbGame.is_new || false,
    rating: Number(dbGame.rating) || 0,
    ratingsCount: dbGame.ratings_count ?? 0,
    playCount: dbGame.play_count ?? 0,
    fakePlayCount: dbGame.fake_play_count,
    createdAt: dbGame.created_at,
    updatedAt: dbGame.updated_at,
    tags: dbGame.tags || [],
    seoTitle: dbGame.seo_title,
    seoDescription: dbGame.seo_description,
    embedType: 'iframe',
    gameUrl: dbGame.game_url || '',
  };
};

const fetchRelatedGames = async (category: string | undefined, gameId: string | undefined): Promise<Game[]> => {
  if (!category || !gameId) return [];
  
  const { data, error } = await supabase
    .from('games')
    .select('*')
    .eq('category', category)
    .neq('id', gameId)
    .eq('hidden', false)
    .limit(6);
  
  if (error) throw error;
  
  return (data || []).map((dbGame): Game => ({
    id: dbGame.id,
    title: dbGame.title,
    description: dbGame.description || '',
    thumbnail: dbGame.thumbnail || '',
    category: dbGame.category,
    featured: dbGame.featured || false,
    trending: dbGame.trending || false,
    isNew: dbGame.is_new || false,
    rating: Number(dbGame.rating) || 0,
    ratingsCount: dbGame.ratings_count ?? 0,
    playCount: dbGame.play_count ?? 0,
    fakePlayCount: dbGame.fake_play_count,
    createdAt: dbGame.created_at,
    updatedAt: dbGame.updated_at,
    tags: dbGame.tags || [],
    embedType: 'iframe',
    gameUrl: dbGame.game_url || '',
  }));
};

export const useGameData = (searchParam: string | undefined, isSlugRoute: boolean = false) => {
  const queryClient = useQueryClient();

  const { data: game, isLoading, error } = useQuery({
    queryKey: ['game', searchParam, isSlugRoute],
    queryFn: () => fetchGame(searchParam, isSlugRoute),
    enabled: !!searchParam,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: (failureCount, error) => {
      // Don't retry if it's a "not found" error
      if (error instanceof Error && error.message === 'Game not found') {
        return false;
      }
      return failureCount < 2;
    }
  });

  const { data: relatedGames = [] } = useQuery({
    queryKey: ['related-games', game?.category, game?.id],
    queryFn: () => fetchRelatedGames(game?.category, game?.id),
    enabled: !!game?.category && !!game?.id,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
  });

  const incrementPlayCount = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('increment_play_count', { game_id: id });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['game', searchParam, isSlugRoute] });
    },
  });

  return { game, isLoading, error, relatedGames, incrementPlayCount };
};
